import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { formatRupiah } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, DollarSign } from "lucide-react";

export default function Rates() {
  const [rates, setRates] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ teacher_id: "", school_id: "", rate_per_session: 0 });

  const load = () => Promise.all([
    api.get("/rates"), api.get("/teachers"), api.get("/schools"),
  ]).then(([r, t, s]) => { setRates(r.data); setTeachers(t.data); setSchools(s.data); });
  useEffect(() => { load(); }, []);

  const tName = (id) => teachers.find((t) => t.id === id)?.name || "-";
  const sName = (id) => schools.find((s) => s.id === id)?.name || "-";

  const openEdit = (r) => { setForm({ teacher_id: r.teacher_id, school_id: r.school_id, rate_per_session: r.rate_per_session }); setDialog(true); };
  const openNew = () => { setForm({ teacher_id: "", school_id: "", rate_per_session: 0 }); setDialog(true); };

  const save = async () => {
    if (!form.teacher_id || !form.school_id) { toast.error("Pilih guru dan sekolah"); return; }
    try {
      await api.post("/rates", { ...form, rate_per_session: Number(form.rate_per_session) });
      toast.success("Tarif tersimpan"); setDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const grouped = schools.map((s) => ({ school: s, items: rates.filter((r) => r.school_id === s.id) }));

  return (
    <div className="space-y-5" data-testid="rates-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">Pengaturan Tarif</h2>
          <p className="text-sm text-muted-foreground">Tarif mengajar per sesi (per guru per sekolah)</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-gradient-to-r from-gtsorange to-gtsorange-light text-white gap-1" data-testid="add-rate-btn">
          <Plus className="w-4 h-4" /> Tambah / Ubah Tarif
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {grouped.map(({ school, items }) => (
          <Card key={school.id} className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] bg-white overflow-hidden" data-testid={`rate-group-${school.id}`}>
            <div className="bg-gtsnavy text-white px-4 py-3 font-nunito font-bold">{school.name}</div>
            <div className="divide-y">
              {items.length === 0 && <p className="p-4 text-sm text-muted-foreground">Belum ada tarif.</p>}
              {items.map((r) => (
                <button key={r.id} onClick={() => openEdit(r)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-orange-50 transition-colors text-left" data-testid={`rate-row-${r.id}`}>
                  <span className="text-sm font-medium text-gtsnavy">{tName(r.teacher_id)}</span>
                  <span className="font-nunito font-bold text-gtsorange">{formatRupiah(r.rate_per_session)}</span>
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tarif Mengajar</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Guru</Label>
              <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="rate-teacher-select"><SelectValue placeholder="Pilih guru" /></SelectTrigger>
                <SelectContent>{teachers.filter((t) => !t.is_staff).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sekolah</Label>
              <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="rate-school-select"><SelectValue placeholder="Pilih sekolah" /></SelectTrigger>
                <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tarif per Sesi (Rp)</Label>
              <Input type="number" value={form.rate_per_session} onChange={(e) => setForm({ ...form, rate_per_session: e.target.value })} className="rounded-xl mt-1" data-testid="rate-amount-input" />
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-gtsnavy text-white" data-testid="save-rate-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
